import { ConfirmationModal } from "@/components/ui/modal/confirmation-modal";
import { useMutation } from "convex/react";
import { api } from "@/convex/_generated/api";
import {
  ReactNode,
  useEffect,
  createContext,
  useContext,
  useCallback,
  useState,
  useRef,
} from "react";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
  SheetFooter,
} from "@/components/ui/sheet";
import {
  Drawer,
  DrawerContent,
  DrawerHeader,
  DrawerTitle,
  DrawerFooter,
  DrawerClose,
} from "@/components/ui/drawer";
import { Button } from "@/components/ui/button";
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { Checkbox } from "@/components/ui/checkbox";
import { Separator } from "@/components/ui/separator";
import { Badge } from "@/components/ui/badge";
import {
  Loader2,
  Info,
  Lock,
  ChevronDownIcon,
  X,
  Minus,
  Plus,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useResponsiveModalVariant,
  type ModalSheetSide,
} from "@/hooks/useResponsiveModalVariant";

const ANIMATION_DURATION = 200; // Duración de la animación del Dialog

// Clases de variante para FormDialog - solo cambios de color sin afectar posicionamiento
const FORM_DIALOG_VARIANTS = {
  default: "bg-accent dark:bg-popover border-border",
  destructive:
    "bg-gradient-to-tl from-red-50 via-red-100/50 to-red-50 dark:bg-popover border-destructive/20",
  secondary:
    "bg-gradient-to-tl from-secondary/30 via-secondary/10 to-secondary/30 dark:bg-popover border-secondary/20",
  outline:
    "bg-gradient-to-tl from-background via-accent/50 to-background dark:bg-popover border-input/50",
  success:
    "bg-gradient-to-tl from-green-50 via-green-100/50 to-green-50 dark:bg-popover border-green-500/20",
  warning:
    "bg-gradient-to-tl from-yellow-50 via-yellow-100/50 to-yellow-50 dark:bg-popover border-yellow-500/20",
  info: "bg-gradient-to-tl from-blue-50 via-blue-100/50 to-blue-50 dark:bg-popover border-blue-500/20",
} as const;

// Context para pasar formId automáticamente a los campos
const FormContext = createContext<{ formId: string }>({ formId: "" });

type FormDialogProps = {
  // Dialog props
  isOpen: boolean;
  onClose: () => void;
  title: string;
  description?: string;

  // Form props
  onSubmit: (e: React.FormEvent<HTMLFormElement>) => void;
  isLoading: boolean;
  children: ReactNode;

  // Actions props (opcionales)
  submitLabel?: string;
  loadingLabel?: string;
  cancelLabel?: string;
  showCancelButton?: boolean;
  showCloseButton?: boolean;
  footerStart?: ReactNode;

  // Layout props (opcionales)
  className?: string;
  maxWidth?:
    | "sm"
    | "md"
    | "lg"
    | "xl"
    | "2xl"
    | "3xl"
    | "4xl"
    | "5xl"
    | "6xl"
    | "7xl";
  maxHeight?: string;
  /** Altura fija para el contenido del drawer en mobile (ej: "400px", "50vh"). Evita saltos de tamaño entre tabs. */
  drawerHeight?: string;
  formId?: string;
  disabledAction?: boolean;
  variant?: keyof typeof FORM_DIALOG_VARIANTS;
  /** Level of nesting for z-index handling (0 = top level, 1+ = nested modals). Default: 0 */
  nestedLevel?: number;
  /**
   * Si es true, consulta la preferencia del usuario (`users.uiModalPreferences`)
   * para decidir entre Dialog/Sheet/Drawer. Si es false (default), mantiene el
   * comportamiento original: <768px => Drawer, >=768px => Dialog.
   */
  respectModalPreference?: boolean;
  /**
   * No enfocar el primer campo al abrir (p. ej. al editar, donde enfocar un
   * campo de relación abre su buscador sin que el usuario lo pida).
   */
  disableAutoFocus?: boolean;
  /**
   * Hay cambios sin guardar: cerrar (Escape, X, Cancelar, clic afuera) pide
   * confirmación antes de descartarlos. Guardar no pregunta (el padre cierra
   * con `onClose`).
   */
  isDirty?: boolean;
  /**
   * Clave con la que se recuerda el ancho del panel lateral que el usuario
   * elige arrastrando su borde (p. ej. una por colección). Sin clave, los
   * formularios comparten el ancho `default`.
   */
  resizeKey?: string;
};

const MAX_WIDTHS = {
  sm: "lg:max-w-sm",
  md: "lg:max-w-md",
  lg: "lg:max-w-lg",
  xl: "lg:max-w-xl",
  "2xl": "lg:max-w-2xl",
  "3xl": "lg:max-w-3xl",
  "4xl": "lg:max-w-4xl",
  "5xl": "lg:max-w-5xl",
  "6xl": "lg:max-w-6xl",
  "7xl": "lg:max-w-7xl",
};

export function FormDialog({
  isOpen,
  onClose,
  title,
  description,
  showCloseButton = true,
  onSubmit,
  isLoading,
  children,
  submitLabel = "Guardar",
  loadingLabel = "Guardando...",
  drawerHeight,
  cancelLabel = "Cancelar",
  showCancelButton = true,
  footerStart,
  maxWidth = "xl",
  maxHeight = "90dvh",
  formId = "form-dialog",
  disabledAction = false,
  className,
  variant = "default",
  nestedLevel = 0,
  respectModalPreference = false,
  disableAutoFocus = false,
  isDirty = false,
  resizeKey = "default",
}: FormDialogProps) {
  const closeTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  // Generar un formId único por instancia para evitar colisiones cuando hay varios formularios abiertos.
  // Usamos un ref combinado con un contador global en `globalThis` y un timestamp corto.
  const instanceIdRef = useRef<string | null>(null);
  if (!instanceIdRef.current) {
    const counter = (globalThis as any).__formDialogInstanceCounter || 0;
    (globalThis as any).__formDialogInstanceCounter = counter + 1;
    instanceIdRef.current = `${formId}-${(globalThis as any).__formDialogInstanceCounter}-${Date.now()
      .toString(36)
      .slice(-6)}`;
  }
  const resolvedFormId = instanceIdRef.current!;
  const [internalIsOpen, setInternalIsOpen] = useState(false);

  // Sincronizar estado interno con prop externa
  useEffect(() => {
    if (isOpen) {
      setInternalIsOpen(true);
    } else {
      setInternalIsOpen(false);
    }
  }, [isOpen]);

  const [confirmDiscard, setConfirmDiscard] = useState(false);

  const handleOpenChange = useCallback(
    (open: boolean, force = false) => {
      // Prevenir cierre durante submit
      if (!open && isLoading) {
        return;
      }
      // Cambios sin guardar: confirmar antes de descartarlos.
      if (!open && isDirty && !force) {
        setConfirmDiscard(true);
        return;
      }

      if (!open) {
        // Iniciar animación de cierre inmediatamente
        setInternalIsOpen(false);

        // Limpiar timeout anterior si existe
        if (closeTimeoutRef.current) {
          clearTimeout(closeTimeoutRef.current);
        }

        // Esperar a que termine la animación antes de llamar onClose
        closeTimeoutRef.current = setTimeout(() => {
          // Limpiar los labels registrados para este formulario cuando el diálogo
          // haya terminado de cerrarse (evita colisiones si se abren otros forms).
          clearFieldLabels(resolvedFormId);
          onClose();
          closeTimeoutRef.current = null;
        }, ANIMATION_DURATION);
      }
    },
    [isLoading, isDirty, onClose],
  );

  const discardModal = (
    <ConfirmationModal
      isOpen={confirmDiscard}
      onClose={() => setConfirmDiscard(false)}
      onConfirm={() => {
        setConfirmDiscard(false);
        handleOpenChange(false, true);
      }}
      title="¿Descartar los cambios?"
      description="Hay cambios sin guardar. Si cierras ahora se pierden."
      confirmText="Descartar"
      cancelText="Seguir editando"
    />
  );

  // Limpiar timeout y los labels del formulario al desmontar
  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      // Asegurarse de limpiar también cualquier label registrado para esta instancia
      // en caso de que el componente se desmonte antes de completar el cierre.
      clearFieldLabels(resolvedFormId);
    };
  }, []);

  const handleSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      e.stopPropagation();
      onSubmit(e);
    },
    [onSubmit],
  );

  // Atajo de teclado: Cmd/Ctrl + Enter dispara el submit del formulario.
  // Centralizado aquí para que todos los dialogs que usan FormDialog lo tengan
  // sin duplicar lógica en cada modal. Usa requestSubmit() para respetar las
  // validaciones nativas del navegador (required, type=email, etc.).
  const isMac =
    typeof navigator !== "undefined" && /Mac/.test(navigator.platform);
  const shortcutLabel = isMac ? "⌘↵" : "Ctrl+↵";

  const handleFormKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLFormElement>) => {
      if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
        e.preventDefault();
        e.currentTarget.requestSubmit();
      }
    },
    [],
  );

  const responsiveVariant = useResponsiveModalVariant();
  // Ancho del panel lateral: el que el usuario dejó al arrastrar su borde
  // (guardado en sus preferencias) o, sin eso, el del `maxWidth`.
  const saveSheetWidth = useMutation(api.system.users.mutations.setSheetWidth);
  const [dragWidth, setDragWidth] = useState<number | null>(null);
  const savedSheetWidth = responsiveVariant.sheetWidths[resizeKey];
  const sheetWidth = dragWidth ?? savedSheetWidth;
    const nativeIsMobile =
      typeof window !== "undefined" && window.innerWidth < 768;
    // Cuando respectModalPreference está activo, mobile web SIEMPRE usa Drawer
    // (UX móvil intacta); solo en escritorio se respeta la preferencia.
    const isMobile = respectModalPreference
      ? responsiveVariant.isMobileViewport || nativeIsMobile
      : nativeIsMobile;
    const shouldUseSheet =
      respectModalPreference && !isMobile && responsiveVariant.shouldRenderSheet;
    const sheetSide: ModalSheetSide = responsiveVariant.sheetSide;
      const sheetOverlayOpacity = responsiveVariant.sheetOverlayOpacity;
      const sheetBackdropBlurPx = responsiveVariant.sheetBackdropBlurPx;

  const formContent = (
    <FormContext.Provider value={{ formId: resolvedFormId }}>
      <form
        id={resolvedFormId}
        onSubmit={handleSubmit}
        onKeyDown={handleFormKeyDown}
        className="flex flex-col flex-1 min-h-0"
      >
        <div className="flex-1 overflow-y-auto px-1 space-y-6">{children}</div>
      </form>
    </FormContext.Provider>
  );

  const submitButton = (
    <Button
      type="submit"
      form={resolvedFormId}
      size="sm"
      disabled={isLoading || disabledAction}
      className="w-full h-7 px-2.5 text-xs transition-opacity duration-200 ease-in-out disabled:opacity-50"
    >
      {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
      {isLoading ? loadingLabel : submitLabel}
      {!isMobile && !isLoading && (
        <kbd className="ml-1 hidden sm:inline-flex h-4 select-none items-center gap-0.5 rounded border border-current/20 bg-background/30 px-1 font-mono text-[10px] font-medium opacity-80">
          {shortcutLabel}
        </kbd>
      )}
    </Button>
  );

  // Track visual viewport height for keyboard handling
  const [viewportHeight, setViewportHeight] = useState<number | null>(null);
  const lastHeightRef = useRef<number | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  useEffect(() => {
    if (!isMobile || !internalIsOpen) return;

    const viewport = window.visualViewport;
    if (!viewport) return;

    const handleResize = () => {
      const newHeight = viewport.height;
      const lastHeight = lastHeightRef.current;

      // Only update if difference is significant (> 100px, typical keyboard height)
      // This prevents small fluctuations from causing glitches
      if (lastHeight === null || Math.abs(newHeight - lastHeight) > 100) {
        // Debounce to avoid rapid updates during keyboard animation
        if (debounceRef.current) {
          clearTimeout(debounceRef.current);
        }
        debounceRef.current = setTimeout(() => {
          lastHeightRef.current = newHeight;
          setViewportHeight(newHeight);
        }, 50);
      }
    };

    // Set initial height
    lastHeightRef.current = viewport.height;
    setViewportHeight(viewport.height);

    viewport.addEventListener("resize", handleResize);

    return () => {
      viewport.removeEventListener("resize", handleResize);
      if (debounceRef.current) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [isMobile, internalIsOpen]);

  if (isMobile) {
    return (
      <>
      <Drawer
        open={internalIsOpen}
        onOpenChange={(open: boolean) => {
          if (!open && !isLoading) handleOpenChange(false);
        }}
        // Prevent accidental close by requiring more drag distance
        preventClose={true}
      >
        <DrawerContent
          onOpenAutoFocus={disableAutoFocus ? (e) => e.preventDefault() : undefined}
          className="flex flex-col fixed bottom-0 transition-[height] duration-150 ease-out"
          largeHandle={false}
          nestedLevel={nestedLevel}
          style={{
            height: viewportHeight ? `${viewportHeight * 0.95}px` : "95dvh",
            maxHeight: "95dvh",
          }}
        >
          {/* Header con padding reducido */}
          <DrawerHeader className="text-left pb-6 pt-0 flex-shrink-0 gap-0">
            <DrawerTitle className="text-base font-semibold">{title}</DrawerTitle>
            {description && (
              <p className="text-[11px] text-muted-foreground line-clamp-1">
                {description}
              </p>
            )}
          </DrawerHeader>

          {/* Contenido del formulario con scroll */}
          <div
            className="flex-1 overflow-y-auto px-4 pb-2 min-h-0"
            style={drawerHeight ? { maxHeight: drawerHeight } : undefined}
          >
            <FormContext.Provider value={{ formId: resolvedFormId }}>
              <form
                id={resolvedFormId}
                onSubmit={handleSubmit}
                onKeyDown={handleFormKeyDown}
                className="space-y-3"
              >
                {children}
              </form>
            </FormContext.Provider>
          </div>

          {/* Footer compacto y fijo - botones en fila horizontal */}
          <DrawerFooter className="pt-2 pb-4 flex-shrink-0 bg-popover !flex-row gap-2">
            {footerStart}
            {showCancelButton && (
              <DrawerClose asChild>
                <Button
                  type="button"
                  size="sm"
                  variant="outline"
                  onClick={() => handleOpenChange(false)}
                  disabled={isLoading}
                  className="flex-1 h-7 px-2.5 text-xs"
                >
                  {cancelLabel}
                </Button>
              </DrawerClose>
            )}
            <div className="flex-1">{submitButton}</div>
          </DrawerFooter>
        </DrawerContent>
      </Drawer>
      {discardModal}
      </>
    );
  }

  if (shouldUseSheet) {
    const resizable = sheetSide === "left" || sheetSide === "right";
    const sheetWidthClass =
      sheetSide === "top" || sheetSide === "bottom"
        ? "w-full sm:max-w-2xl"
        : resizable && sheetWidth
          ? "w-full sm:max-w-none"
          : cn("w-full", MAX_WIDTHS[maxWidth] || "sm:max-w-md");
    return (
      <>
      <Sheet open={internalIsOpen} onOpenChange={handleOpenChange}>
        <SheetContent
          side={sheetSide}
          onOpenAutoFocus={disableAutoFocus ? (e) => e.preventDefault() : undefined}
          className={cn(
            "flex flex-col gap-0 p-0",
            sheetWidthClass,
            // Mismo tinte de variante que la rama Dialog (línea del DialogContent
            // abajo). Sin esto, el Sheet se quedaba con `bg-background` mientras
            // el Dialog usaba `bg-accent`/gradiente del variant → colores de
            // fondo distintos para el mismo modal según la preferencia.
            FORM_DIALOG_VARIANTS[variant || "default"],
            className,
          )}
          showCloseButton={showCloseButton}
          overlayOpacity={sheetOverlayOpacity}
          overlayBlurPx={sheetBackdropBlurPx}
          style={{
            maxHeight: "100dvh",
            ...(resizable && sheetWidth ? { width: sheetWidth, maxWidth: "calc(100vw - 48px)" } : {}),
          }}
        >
          {resizable && (
            <SheetResizeHandle
              side={sheetSide}
              onResize={setDragWidth}
              onCommit={(width) => {
                void saveSheetWidth({ key: resizeKey, widthPx: width }).catch(() => undefined);
              }}
              onReset={() => {
                setDragWidth(null);
                void saveSheetWidth({ key: resizeKey, widthPx: null }).catch(() => undefined);
              }}
            />
          )}
          {/* Header fijo */}
          <SheetHeader className="flex-shrink-0 px-4 pt-4 pb-6 text-left gap-0">
            <SheetTitle className="text-base font-semibold">{title}</SheetTitle>
            {description && <SheetDescription className="text-xs">{description}</SheetDescription>}
          </SheetHeader>

          <FormContext.Provider value={{ formId: resolvedFormId }}>
            <form
              id={resolvedFormId}
              onSubmit={handleSubmit}
              onKeyDown={handleFormKeyDown}
              className="flex flex-col flex-1 min-h-0"
            >
              {/* Contenido del formulario con scroll */}
              <div className="flex-1 overflow-y-auto px-4 space-y-6">
                {children}
              </div>

              {/* Botones de acción fijos */}
              <SheetFooter className="flex-shrink-0 flex flex-row justify-between gap-2 px-4 pt-4 pb-2">
                <div>{footerStart}</div>
                <div className="flex gap-2">
                  {showCancelButton && (
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      onClick={() => handleOpenChange(false)}
                      disabled={isLoading}
                      className="h-7 px-2.5 text-xs"
                    >
                      {cancelLabel}
                    </Button>
                  )}
                  <Button
                    type="submit"
                    size="sm"
                    disabled={isLoading || disabledAction}
                    className="h-7 px-2.5 text-xs transition-opacity duration-200 ease-in-out disabled:opacity-50"
                  >
                    {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                    {isLoading ? loadingLabel : submitLabel}
                    {!isLoading && (
                      <kbd className="ml-1 hidden sm:inline-flex h-4 select-none items-center gap-0.5 rounded border border-current/20 bg-background/30 px-1 font-mono text-[10px] font-medium opacity-80">
                        {shortcutLabel}
                      </kbd>
                    )}
                  </Button>
                </div>
              </SheetFooter>
            </form>
          </FormContext.Provider>
        </SheetContent>
      </Sheet>
      {discardModal}
      </>
    );
  }

  return (
    <>
    <Dialog open={internalIsOpen} onOpenChange={handleOpenChange}>
      <DialogContent
        onOpenAutoFocus={disableAutoFocus ? (e) => e.preventDefault() : undefined}
        className={cn(
          MAX_WIDTHS[maxWidth],
          "flex flex-col gap-0 p-0",
          FORM_DIALOG_VARIANTS[variant || "default"],
          className,
        )}
        style={{ maxHeight }}
        showCloseButton={showCloseButton}
        nestedLevel={nestedLevel}
      >
        {/* Header fijo */}
        <DialogHeader className="flex-shrink-0 px-4 pt-4 pb-6 text-left gap-0">
          <DialogTitle className="text-base font-semibold">{title}</DialogTitle>
          {description && <DialogDescription className="text-xs">{description}</DialogDescription>}
        </DialogHeader>

        <FormContext.Provider value={{ formId: resolvedFormId }}>
          <form
            id={resolvedFormId}
            onSubmit={handleSubmit}
            onKeyDown={handleFormKeyDown}
            className="flex flex-col flex-1 min-h-0"
          >
            {/* Contenido del formulario con scroll */}
            <div className="flex-1 overflow-y-auto px-4 space-y-6">
              {children}
            </div>

            {/* Botones de acción fijos */}
            <div className="flex-shrink-0 flex flex-row justify-between gap-2 px-4 pt-4 pb-2">
              <div>{footerStart}</div>
              <div className="flex gap-2">
                {showCancelButton && (
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    onClick={() => handleOpenChange(false)}
                    disabled={isLoading}
                    className="h-7 px-2.5 text-xs"
                  >
                    {cancelLabel}
                  </Button>
                )}
                <Button
                  type="submit"
                  size="sm"
                  disabled={isLoading || disabledAction}
                  className="h-7 px-2.5 text-xs transition-opacity duration-200 ease-in-out disabled:opacity-50"
                >
                  {isLoading && <Loader2 className="h-3.5 w-3.5 animate-spin" />}
                  {isLoading ? loadingLabel : submitLabel}
                  {!isLoading && (
                    <kbd className="ml-1 hidden sm:inline-flex h-4 select-none items-center gap-0.5 rounded border border-current/20 bg-background/30 px-1 font-mono text-[10px] font-medium opacity-80">
                      {shortcutLabel}
                    </kbd>
                  )}
                </Button>
              </div>
            </div>
          </form>
        </FormContext.Provider>
      </DialogContent>
    </Dialog>
    {discardModal}
    </>
  );
}

import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import * as Icons from "lucide-react";

import DynamicIcon from "@/components/utils/lucide-icon";
import {
  registerFieldLabel,
  clearFieldLabels,
} from "@/hooks/useFormValidation";
import { Skeleton } from "../skeleton";

type IconName = keyof typeof Icons;

// ========== FIELD WRAPPER ==========
type FormFieldProps = {
  label: string | ReactNode;
  htmlFor: string;
  required?: boolean;
  children: ReactNode;
  helperText?: string;
  error?: string;
  icon?: IconName;
  tooltip?: string;
  showErrorAsTooltip?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  pattern?: string;
  isLoading?: boolean;
};

export function FormField({
  label,
  htmlFor,
  required = false,
  children,
  helperText,
  error,
  icon,
  tooltip,
  showErrorAsTooltip = true,
  disabled,
  hidden,
  pattern,
  isLoading,
}: FormFieldProps) {
  // Obtener formId del contexto automáticamente
  const { formId } = useContext(FormContext);

  // Registrar el label automáticamente cuando el componente se monta
  useEffect(() => {
    // Si label es un ReactNode, extraer el texto o usar un placeholder
    const labelString = typeof label === "string" ? label : "Field";
    registerFieldLabel(htmlFor, labelString, formId);
  }, [htmlFor, label, formId]);

  // helperText se convierte en tooltip por defecto
  // Si hay error y showErrorAsTooltip está activo, el error tiene prioridad
  const tooltipText =
    showErrorAsTooltip && error ? error : tooltip || helperText;

  // Determinar color del tooltip según si es error o info
  const tooltipIconColor =
    showErrorAsTooltip && error
      ? "text-yellow-600 hover:text-yellow-700 dark:text-yellow-500 dark:hover:text-yellow-600"
      : "text-muted-foreground hover:text-foreground";

  return (
    <div
      className={`space-y-2 ${isLoading && "animate-pulse"}`}
      hidden={hidden}
    >
      <Label
        htmlFor={htmlFor}
        className={cn(
          "flex items-center text-xs text-foreground/90",
          tooltipText ? "justify-between" : "gap-2",
        )}
      >
        <span className="flex items-center gap-1.5">
          {icon && <DynamicIcon name={icon} className="w-3.5 h-3.5" />}
          {label}
          {required && !tooltipText && (
            <Lock className="h-3 w-3 text-primary" />
          )}
        </span>
        {tooltipText && (
          <span className="flex items-center gap-1">
            {required && <Lock className="h-3 w-3 text-primary" />}
            <Tooltip>
              <TooltipTrigger
                type="button"
                tabIndex={-1}
                className="inline-flex cursor-help"
              >
                <Info
                  className={cn("w-3.5 h-3.5 transition-colors", tooltipIconColor)}
                />
              </TooltipTrigger>
              <TooltipContent
                side="top"
                className="max-w-xs z-[9999]"
                sideOffset={5}
              >
                <p>{tooltipText}</p>
              </TooltipContent>
            </Tooltip>
          </span>
        )}
      </Label>
      {children}
      {!showErrorAsTooltip && error && (
        <p className="text-sm text-destructive">{error}</p>
      )}
    </div>
  );
}

// ========== INPUT FIELD ==========
type InputFieldProps = {
  label: string;
  name: string;
  type?: string;
  placeholder?: string;
  defaultValue?: string;
  value?: string; // ✅ Agregar value para componente controlado
  required?: boolean;
  helperText?: string;
  error?: string;
  className?: string;
  icon?: IconName;
  tooltip?: string;
  onChange?: (e: React.ChangeEvent<HTMLInputElement>) => void;
  readOnly?: boolean;
  disabled?: boolean;
  hidden?: boolean;
  isLoading?: boolean;
  ref?: React.Ref<HTMLInputElement>;
  onKeyDown?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
  onBlur?: (e: React.FocusEvent<HTMLInputElement>) => void;
  /** Mobile keyboard type hint: numeric, tel, email, url, search, decimal, none */
  inputMode?:
    | "none"
    | "text"
    | "tel"
    | "url"
    | "email"
    | "numeric"
    | "decimal"
    | "search";
};

export function InputField({
  label,
  name,
  type = "text",
  placeholder,
  defaultValue,
  value, // ✅ Agregar value
  required = false,
  helperText,
  error,
  className,
  icon,
  tooltip,
  onChange,
  readOnly = false,
  disabled = false,
  hidden = false,
  isLoading = false,
  ref,
  onKeyDown,
  onBlur,
  inputMode,
}: InputFieldProps) {
  // Auto-detect inputMode based on type if not explicitly provided
  const resolvedInputMode =
    inputMode ??
    (type === "email"
      ? "email"
      : type === "tel"
        ? "tel"
        : type === "url"
          ? "url"
          : type === "number"
            ? "decimal"
            : undefined);
  return (
    <FormField
      label={label}
      htmlFor={name}
      required={required}
      helperText={helperText}
      error={error}
      icon={icon}
      tooltip={tooltip}
      hidden={hidden}
    >
      {!isLoading ? (
        <Input
          ref={ref}
          id={name}
          name={name}
          type={type}
          inputMode={resolvedInputMode}
          onKeyDown={onKeyDown}
          onBlur={onBlur}
          placeholder={placeholder}
          {...(value !== undefined ? { value } : { defaultValue })}
          required={required}
          className={`${className} ${isLoading && " animate-pulse"} z-10`}
          onChange={onChange}
          readOnly={readOnly}
          disabled={disabled}
          hidden={hidden}
        />
      ) : (
        <div className=" border-[0.5] border-gray-300 dark:border-gray-700 bg-background rounded-lg dark:bg-input/30">
          <Skeleton className="w-full h-8 " />
        </div>
      )}
    </FormField>
  );
}

type MaskType = "currency" | "percentage" | "phone" | "custom" | "none";

type NumberInputFieldProps = {
  label: string;
  name: string;
  placeholder?: string;
  defaultValue?: number;
  value?: number;
  required?: boolean;
  helperText?: string;
  error?: string;
  className?: string;
  icon?: any;
  tooltip?: string;
  min?: number;
  max?: number;
  step?: number;
  decimals?: number;
  allowNegative?: boolean;
  onChange?: (value: number | undefined) => void;
  disabled?: boolean;
  hidden?: boolean;
  pattern?: string;
  oninput?: (value: number | undefined) => void;
  mask?: MaskType;
  currencySymbol?: string;
  thousandsSeparator?: string;
  decimalSeparator?: string;
  customMask?: (value: string) => string;
  prefix?: string;
  suffix?: string;
  maxLength?: number;
  forceStepper?: boolean;
};

export function NumberInputField({
  label,
  name,
  placeholder,
  defaultValue,
  value,
  required = false,
  helperText,
  error,
  className,
  icon,
  tooltip,
  min,
  max,
  step = 1,
  decimals = 2,
  allowNegative = true,
  onChange,
  disabled,
  hidden,
  pattern,
  oninput,
  mask = "none",
  currencySymbol = "$",
  thousandsSeparator = ",",
  decimalSeparator = ".",
  customMask,
  prefix = "",
  suffix = "",
  maxLength,
  forceStepper = false,
}: NumberInputFieldProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isEditing, setIsEditing] = useState(false);
  const [localValue, setLocalValue] = useState("");

  const cleanValue = (val: string): string => {
    return val.replace(/[^\d.-]/g, "");
  };

  const applyCurrencyMask = (val: string): string => {
    const cleaned = cleanValue(val);
    if (cleaned === "" || cleaned === "-") return cleaned;

    const numValue = parseFloat(cleaned);
    if (isNaN(numValue)) return "";

    const parts = Math.abs(numValue).toFixed(decimals).split(".");
    parts[0] = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, thousandsSeparator);

    const formatted = parts.join(decimalSeparator);
    return `${numValue < 0 ? "-" : ""}${currencySymbol}${formatted}`;
  };

  const applyPercentageMask = (val: string): string => {
    const cleaned = cleanValue(val);
    if (cleaned === "" || cleaned === "-") return cleaned;

    const numValue = parseFloat(cleaned);
    if (isNaN(numValue)) return "";

    return `${numValue.toFixed(decimals)}%`;
  };

  const applyPhoneMask = (val: string): string => {
    const cleaned = val.replace(/\D/g, "");
    const match = cleaned.match(/^(\d{0,3})(\d{0,3})(\d{0,4})$/);
    if (match) {
      return [
        match[1] ? "(" : "",
        match[1],
        match[1] && match[2] ? ") " : "",
        match[2],
        match[2] && match[3] ? "-" : "",
        match[3],
      ].join("");
    }
    return val;
  };

  const applyMask = (val: string): string => {
    if (mask === "none") return val;

    switch (mask) {
      case "currency":
        return applyCurrencyMask(val);
      case "percentage":
        return applyPercentageMask(val);
      case "phone":
        return applyPhoneMask(val);
      case "custom":
        return customMask ? customMask(val) : val;
      default:
        return val;
    }
  };

  const getDisplayValue = (): string => {
    if (isEditing) {
      return localValue;
    }
    if (value !== undefined) {
      return applyMask(value.toString());
    }
    if (defaultValue !== undefined) {
      return applyMask(defaultValue.toString());
    }
    return "";
  };

  const displayValue = getDisplayValue();

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const inputValue = e.target.value;

    // Rechazar entrada si contiene letras (excepto para máscaras especiales)
    if (mask !== "phone" && /[a-zA-Z]/.test(inputValue)) {
      return;
    }

    const cleaned = cleanValue(inputValue);

    if (maxLength !== undefined) {
      const digitsOnly = cleaned.replace(/[^\d]/g, "");
      if (digitsOnly.length > maxLength) {
        return;
      }
    }

    setLocalValue(inputValue);

    if (cleaned === "" || cleaned === "-") {
      onChange?.(undefined);
      return;
    }

    let numValue = parseFloat(cleaned);

    if (isNaN(numValue)) {
      onChange?.(undefined);
      return;
    }

    if (!allowNegative && numValue < 0) {
      numValue = 0;
    }

    if (min !== undefined && numValue < min) {
      numValue = min;
    }
    if (max !== undefined && numValue > max) {
      numValue = max;
    }

    if (decimals !== undefined) {
      numValue = parseFloat(numValue.toFixed(decimals));
    }

    onChange?.(numValue);
  };

  const handleBlur = () => {
    setIsEditing(false);
    setLocalValue("");
  };

  const handleFocus = (e: React.FocusEvent<HTMLInputElement>) => {
    setIsEditing(true);
    const currentValue =
      value !== undefined
        ? value.toString()
        : defaultValue !== undefined
          ? defaultValue.toString()
          : "";
    setLocalValue(currentValue);
    setTimeout(() => e.target.select(), 0);
  };

  const stepValue = useCallback(
    (direction: 1 | -1) => {
      const current = value !== undefined ? value : (defaultValue ?? 0);
      let next = current + direction * (step ?? 1);
      if (min !== undefined) next = Math.max(min, next);
      if (max !== undefined) next = Math.min(max, next);
      next = parseFloat(next.toFixed(decimals));
      onChange?.(next);
    },
    [value, step, min, max, decimals, defaultValue, onChange],
  );

  const isMobile = forceStepper || window.innerWidth < 768;

  const innerInput = (
    <Input
      ref={inputRef}
      id={name}
      name={name}
      type={mask === "phone" ? "tel" : "text"}
      pattern={pattern}
      placeholder={placeholder}
      value={displayValue}
      required={required}
      className={cn(
        className,
        isMobile &&
          mask !== "phone" &&
          "border-none shadow-none rounded-none focus-visible:ring-0 text-center h-9",
      )}
      onChange={handleChange}
      onBlur={handleBlur}
      onFocus={handleFocus}
      disabled={disabled}
      hidden={hidden}
      inputMode={mask === "phone" ? "tel" : "decimal"}
    />
  );

  return (
    <FormField
      label={label}
      htmlFor={name}
      required={required}
      helperText={helperText}
      error={error}
      icon={icon}
      tooltip={tooltip}
      disabled={disabled}
      hidden={hidden}
      pattern={pattern}
    >
      {isMobile && mask !== "phone" ? (
        <div
          className={cn(
            "flex items-center rounded-md border border-ring/50 overflow-hidden",
            "shadow-[0_2px_6px_rgba(0,0,0,0.06)]",
            disabled && "opacity-50 pointer-events-none",
          )}
        >
          <button
            type="button"
            onClick={() => stepValue(-1)}
            disabled={
              disabled ||
              (min !== undefined &&
                parseFloat(cleanValue(displayValue) || "0") <= min)
            }
            className="h-9 w-9 flex items-center justify-center bg-muted/60 hover:bg-muted active:bg-muted/40 border-r border-ring/30 flex-shrink-0 text-muted-foreground transition-colors disabled:opacity-40"
          >
            <Minus className="h-3.5 w-3.5" />
          </button>
          {innerInput}
          <button
            type="button"
            onClick={() => stepValue(1)}
            disabled={
              disabled ||
              (max !== undefined &&
                parseFloat(cleanValue(displayValue) || "0") >= max)
            }
            className="h-9 w-9 flex items-center justify-center bg-muted/60 hover:bg-muted active:bg-muted/40 border-l border-ring/30 flex-shrink-0 text-muted-foreground transition-colors disabled:opacity-40"
          >
            <Plus className="h-3.5 w-3.5" />
          </button>
        </div>
      ) : (
        innerInput
      )}
    </FormField>
  );
}

// ========== TEXTAREA FIELD ==========
type TextareaFieldProps = {
  label: string;
  name: string;
  placeholder?: string;
  defaultValue?: string;
  required?: boolean;
  helperText?: string;
  error?: string;
  rows?: number;
  icon?: IconName;
  tooltip?: string;
  onChange?: (e: React.ChangeEvent<HTMLTextAreaElement>) => void;
  cols?: number;
};

export function TextareaField({
  label,
  name,
  placeholder,
  defaultValue,
  required = false,
  helperText,
  error,
  rows = 3,
  icon,
  tooltip,
  onChange,
  cols = 1,
}: TextareaFieldProps) {
  return (
    <FormField
      label={label}
      htmlFor={name}
      required={required}
      helperText={helperText}
      error={error}
      icon={icon}
      tooltip={tooltip}
    >
      <Textarea
        id={name}
        name={name}
        placeholder={placeholder}
        defaultValue={defaultValue}
        required={required}
        rows={rows}
        onChange={onChange}
        cols={cols}
      />
    </FormField>
  );
}

// ========== SELECT FIELD ==========
type SelectOption = {
  value: string;
  label: string;
  icon?: IconName;
};

type SelectFieldProps = {
  label: string;
  name: string;
  options: SelectOption[];
  defaultValue?: string;
  placeholder?: string;
  required?: boolean;
  helperText?: string;
  error?: string;
  icon?: IconName;
  tooltip?: string;
  onValueChange?: (value: string) => void;
  disabled?: boolean;
  value?: string;
  hidden?: boolean;
  className?: string;
};

export function SelectField({
  label,
  name,
  options,
  placeholder = "Selecciona una opción",
  required = false,
  helperText,
  error,
  icon,
  tooltip,
  disabled = false,
  value: controlledValue,
  onValueChange: controlledOnValueChange,
  defaultValue,
  hidden = false,
  className,
}: SelectFieldProps) {
  const isControlled = controlledValue !== undefined;
  const [internalValue, setInternalValue] = useState(defaultValue || "");

  const currentValue = isControlled ? controlledValue : internalValue;

  const handleValueChange = (value: string) => {
    if (isControlled && controlledOnValueChange) {
      controlledOnValueChange(value);
    } else if (!isControlled) {
      setInternalValue(value);
    }
  };

  return (
    <FormField
      label={label}
      htmlFor={name}
      required={required}
      helperText={helperText}
      error={error}
      icon={icon}
      tooltip={tooltip}
      hidden={hidden}
    >
      <Select
        value={currentValue}
        onValueChange={handleValueChange}
        disabled={disabled}
        required={required}
      >
        <SelectTrigger id={name}>
          <SelectValue placeholder={placeholder} />
        </SelectTrigger>
        <SelectContent className={className}>
          {options.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.icon && (
                <DynamicIcon name={option.icon} className="mr-2" />
              )}
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      <input type="hidden" name={name} value={currentValue} />
    </FormField>
  );
}

// ========== FIELD GROUP ==========
type FieldGroupProps = {
  title?: string;
  icon?: ReactNode;
  children: ReactNode;
  columns?: 1 | 2;
  className?: string;
};

export function FieldGroup({
  title,
  icon,
  children,
  columns = 1,
  className = "",
}: FieldGroupProps) {
  return (
    <div className={`space-y-4 ${className}`}>
      {(title || icon) && (
        <h2 className="text-sm font-medium flex items-center gap-1.5 [&_svg]:w-3.5 [&_svg]:h-3.5 text-foreground/90">
          {icon}
          {title}
        </h2>
      )}
      <div
        className={columns === 2 ? "grid gap-4 md:grid-cols-2" : "space-y-4"}
      >
        {children}
      </div>
    </div>
  );
}

import type { KeyboardEvent } from "react";

interface ArrayInputFieldProps {
  label: string;
  name: string;
  placeholder?: string;
  value?: string[];
  onChange?: (values: string[]) => void;
  required?: boolean;
  helperText?: string;
  error?: string;
  className?: string;
  icon?: IconName;
  tooltip?: string;
  separator?: "," | "Enter" | "both"; // Define cómo separar los valores
  allowDuplicates?: boolean;
  prefix?: string;
  suffix?: string;
}

export function ArrayInputField({
  label,
  name,
  placeholder = "Escribe y presiona Enter...",
  value = [],
  onChange,
  required = false,
  helperText,
  error,
  className,
  icon,
  tooltip,
  separator = "both",
  allowDuplicates = false,
  prefix,
  suffix,
}: ArrayInputFieldProps) {
  const [inputValue, setInputValue] = useState("");
  const inputRef = useRef<HTMLInputElement>(null);

  const addValue = (newValue: string) => {
    const trimmedValue = newValue.trim();
    if (!trimmedValue) return;

    // Verificar duplicados si no están permitidos
    if (!allowDuplicates && value.includes(trimmedValue)) {
      setInputValue("");
      return;
    }

    const newValues = [...value, trimmedValue];
    onChange?.(newValues);
    setInputValue("");
  };

  const removeValue = (indexToRemove: number) => {
    const newValues = value.filter((_, index) => index !== indexToRemove);
    onChange?.(newValues);
  };

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    // Agregar con Enter
    if (e.key === "Enter" && (separator === "Enter" || separator === "both")) {
      e.preventDefault();
      addValue(inputValue);
    }
    // Agregar con coma
    else if (e.key === "," && (separator === "," || separator === "both")) {
      e.preventDefault();
      addValue(inputValue);
    }
    // Eliminar último elemento con Backspace
    else if (e.key === "Backspace" && inputValue === "" && value.length > 0) {
      e.preventDefault();
      removeValue(value.length - 1);
    }
  };

  const handleInputBlur = () => {
    if (inputValue.trim()) {
      addValue(inputValue);
    }
  };

  const helperMessage =
    helperText ||
    (separator === "both"
      ? "Presiona Enter o coma para agregar"
      : separator === "Enter"
        ? "Presiona Enter para agregar"
        : "Presiona coma para agregar");

  return (
    <FormField
      label={label}
      htmlFor={name}
      required={required}
      helperText={helperMessage}
      error={error}
      icon={icon}
      tooltip={tooltip}
    >
      <div
        className={`flex flex-wrap gap-2 min-h-[2.5rem] p-2 border rounded-md bg-background ${
          error ? "border-destructive" : "border-input"
        } ${className || ""}`}
      >
        {value.map((item, index) => (
          <Badge
            key={index}
            variant="secondary"
            className="flex items-center gap-1"
          >
            <span className="max-w-50 truncate">{`${!prefix ? "" : prefix}${item}${!suffix ? "" : suffix}`}</span>
            <button
              type="button"
              onClick={() => removeValue(index)}
              className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
            >
              <X className="h-3 w-3" />
            </button>
          </Badge>
        ))}
        <Input
          ref={inputRef}
          id={name}
          name={name}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          onBlur={handleInputBlur}
          placeholder={value.length === 0 ? placeholder : "Agregar"}
          className="border-none shadow-none focus-visible:ring-0 flex-1 min-w-30 h-auto px-1 py-0 placeholder:text-xs"
          required={required && value.length === 0}
        />
      </div>
    </FormField>
  );
}

interface SelectMultipleFieldProps {
  label: string;
  name: string;
  options: SelectOption[];
  placeholder?: string;
  value?: string | string[];
  onChange?: (value: string) => void;
  required?: boolean;
  helperText?: string;
  error?: string;
  icon?: IconName;
  tooltip?: string;
  disabled?: boolean;
  defaultValue?: string | string[];
  className?: string;
}

export function SelectMultipleField({
  label,
  name,
  options,
  placeholder = "Selecciona opciones...",
  value = [],
  onChange,
  required = false,
  helperText,
  error,
  icon,
  tooltip,
  disabled = false,
  defaultValue,
  className,
}: SelectMultipleFieldProps) {
  const [isOpen, setIsOpen] = useState(false);

  // Convertir value a array de strings
  const getSelectedValues = (): string[] => {
    if (Array.isArray(value)) {
      return value;
    }
    if (typeof value === "string") {
      return value ? value.split(",").filter((v) => v.trim() !== "") : [];
    }
    return [];
  };

  const [selectedValues, setSelectedValues] =
    useState<string[]>(getSelectedValues());

  // Sincronizar con value prop
  useEffect(() => {
    setSelectedValues(getSelectedValues());
  }, [value]);

  const handleToggle = (optionValue: string) => {
    const newValues = selectedValues.includes(optionValue)
      ? selectedValues.filter((v) => v !== optionValue)
      : [...selectedValues, optionValue];

    setSelectedValues(newValues);
    // Enviar como string separado por comas para compatibilidad con formularios
    onChange?.(newValues.join(","));
  };

  const handleSelectAll = () => {
    const allValues = options.map((opt) => opt.value);
    setSelectedValues(allValues);
    onChange?.(allValues.join(","));
  };

  const handleClearAll = () => {
    setSelectedValues([]);
    onChange?.("");
  };

  const selectedLabels = selectedValues
    .map((val) => options.find((opt) => opt.value === val)?.label || val)
    .join(", ");

  return (
    <FormField
      label={label}
      htmlFor={name}
      required={required}
      helperText={helperText}
      error={error}
      icon={icon}
      tooltip={tooltip}
    >
      <Popover open={isOpen} onOpenChange={setIsOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            aria-expanded={isOpen}
            className={`w-full justify-between ${error ? "border-destructive" : ""} ${className || ""}`}
            disabled={disabled}
          >
            <span className="truncate">
              {selectedValues.length > 0
                ? selectedLabels || placeholder
                : placeholder}
            </span>
            <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-full p-0 z-[1002]" align="start">
          <div className="space-y-2 p-2">
            <div className="flex justify-between items-center px-2">
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleSelectAll}
                className="h-7 text-xs"
              >
                Seleccionar todos
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                onClick={handleClearAll}
                className="h-7 text-xs"
              >
                Limpiar
              </Button>
            </div>
            <Separator />
            <div
              className="max-h-60 overflow-y-auto"
              style={{
                scrollbarWidth: "thin",
                scrollbarColor: "hsl(var(--border)) transparent",
              }}
              onWheel={(e) => {
                // Permitir scroll con rueda del mouse
                e.currentTarget.scrollTop += e.deltaY;
              }}
            >
              {options.map((option) => (
                <div
                  key={option.value}
                  className="flex items-center space-x-2 p-2 hover:bg-accent rounded-sm"
                >
                  <Checkbox
                    checked={selectedValues.includes(option.value)}
                    onCheckedChange={() => handleToggle(option.value)}
                    id={`${name}-${option.value}`}
                  />
                  <label
                    htmlFor={`${name}-${option.value}`}
                    className="text-sm cursor-pointer flex-1 py-2 px-2 -my-2 -mx-2 rounded-sm hover:bg-accent/50 transition-colors"
                  >
                    {option.icon && (
                      <DynamicIcon
                        name={option.icon}
                        className="mr-2 h-4 w-4 inline"
                      />
                    )}
                    {option.label}
                  </label>
                </div>
              ))}
            </div>
          </div>
        </PopoverContent>
      </Popover>

      {/* Hidden input para el formulario */}
      <input type="hidden" name={name} value={selectedValues.join(",")} />

      {/* Badges para mostrar seleccionados */}
      {selectedValues.length > 0 && (
        <div className="flex flex-wrap gap-1 mt-2">
          {selectedValues.map((val) => {
            const option = options.find((opt) => opt.value === val);
            return (
              <Badge key={val} variant="secondary" className="text-xs">
                {option?.label || val}
                <button
                  type="button"
                  onClick={() => handleToggle(val)}
                  className="ml-1 hover:bg-destructive/20 rounded-full p-0.5"
                >
                  <X className="h-3 w-3" />
                </button>
              </Badge>
            );
          })}
        </div>
      )}
    </FormField>
  );
}

/**
 * Borde del panel lateral para cambiar su ancho: se arrastra (o con las
 * flechas del teclado) y al soltar se guarda; doble clic vuelve al ancho por
 * defecto.
 */
function SheetResizeHandle({
  side,
  onResize,
  onCommit,
  onReset,
}: {
  side: ModalSheetSide;
  onResize: (width: number) => void;
  onCommit: (width: number) => void;
  onReset: () => void;
}) {
  const start = useRef<{ x: number; width: number; last: number } | null>(null);
  const clampWidth = (w: number) => Math.round(Math.min(window.innerWidth - 48, Math.max(360, w)));
  const panelWidth = (el: HTMLElement) => el.parentElement?.getBoundingClientRect().width ?? 480;
  return (
    <div
      role="separator"
      aria-orientation="vertical"
      aria-label="Ancho del panel: arrastra o usa las flechas; doble clic para restablecer"
      tabIndex={0}
      onPointerDown={(e) => {
        e.preventDefault();
        e.currentTarget.setPointerCapture(e.pointerId);
        const width = panelWidth(e.currentTarget);
        start.current = { x: e.clientX, width, last: width };
      }}
      onPointerMove={(e) => {
        if (!start.current) return;
        const delta = side === "right" ? start.current.x - e.clientX : e.clientX - start.current.x;
        start.current.last = clampWidth(start.current.width + delta);
        onResize(start.current.last);
      }}
      onPointerUp={(e) => {
        if (!start.current) return;
        e.currentTarget.releasePointerCapture(e.pointerId);
        const { width, last } = start.current;
        start.current = null;
        if (Math.abs(last - width) >= 2) onCommit(last);
      }}
      onDoubleClick={onReset}
      onKeyDown={(e) => {
        if (e.key !== "ArrowLeft" && e.key !== "ArrowRight") return;
        e.preventDefault();
        const grow = (e.key === "ArrowLeft") === (side === "right");
        const width = clampWidth(panelWidth(e.currentTarget) + (grow ? 32 : -32));
        onResize(width);
        onCommit(width);
      }}
      className={cn(
        "group absolute inset-y-0 z-20 flex w-2 cursor-col-resize touch-none items-center justify-center focus-visible:outline-none",
        side === "right" ? "-left-1" : "-right-1",
      )}
    >
      <span className="h-12 w-1 rounded-full bg-border opacity-0 transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100 group-active:bg-primary group-active:opacity-100" />
    </div>
  );
}
